import React, { useState, useRef, useEffect } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import SixDofSim from '../lib/6dof'

import {
  grainsToKg,
  gcm2ToKgm2,
  inchesToMeters,
  fpsToMps,
  mpsToFps,
  inhgToPa,
  fToK,
  mphToMps,
  metersToYards,
  metersToInches,
  degToRad,
  radToDeg
} from '../lib/conversions'

const examples = {
  'M855': {
    mass: '62',
    Ix: '.1416',
    Iy: '1.138',
    diameter: '0.224',
    twistRate: '7',
    v0: '3000',
    Cd: [[1.664,0.387],[1.678,0.384],[1.686,0.374],[1.687,0.372],[1.689,0.371],[1.702,0.385],[1.723,0.379],[1.725,0.368],[2.172,0.328],[2.212,0.332],[2.215,0.325],[2.224,0.321],[2.23,0.318],[2.248,0.325],[2.25,0.328],[2.252,0.317],[2.612,0.298],[2.613,0.294],[2.617,0.304],[2.631,0.293],[2.632,0.292]],
    Cda2: [[1.664,5.4],[1.678,5.4],[1.686,7.9],[1.687,5.2],[1.689,7.9],[1.702,5.5],[1.723,6.5],[1.725,7.6],[2.172,6.3],[2.212,6],[2.215,5.3],[2.224,6],[2.23,5.8],[2.248,6],[2.25,6.2],[2.252,6.4],[2.612,5.6],[2.613,6],[2.617,5.8],[2.631,3.9],[2.632,6]],
    Cla: [[1.664,2.72],[1.678,2.71],[1.686,2.57],[1.687,2.51],[1.689,2.6],[1.702,2.8],[1.723,2.33],[1.725,2.35],[2.172,2.56],[2.212,2.63],[2.215,2.64],[2.224,2.68],[2.23,2.64],[2.248,2.67],[2.25,2.83],[2.252,2.63],[2.612,2.33],[2.613,2.71],[2.617,2.5],[2.631,2.59],[2.632,2.71]],
    Cla2: [[1.664,-2.3],[1.678,-2.3],[1.686,-4.9],[1.687,-2.3],[1.689,-4.9],[1.702,-2.3],[1.723,-3.8],[1.725,-4.9],[2.172,-3.4],[2.212,-3],[2.215,-2.3],[2.224,-3],[2.23,-2.9],[2.248,-3],[2.25,-3],[2.252,-3.4],[2.612,-3],[2.613,-3],[2.617,-3],[2.631,-1],[2.632,-3]],
    Cypa: [[1.664,-0.5],[1.678,-0.5],[1.686,-0.5],[1.687,-0.5],[1.689,-0.5],[1.702,-0.5],[1.723,-0.5],[1.725,-0.5],[2.172,-0.5],[2.212,-0.5],[2.215,-0.5],[2.224,-0.5],[2.23,-0.5],[2.248,-0.5],[2.25,-0.5],[2.252,-0.5],[2.612,-0.5],[2.613,-0.5],[2.617,-0.5],[2.631,-0.5],[2.632,-0.5]],
    Cypa2: [[1.664,0],[1.678,0],[1.686,0],[1.687,0],[1.689,0],[1.702,0],[1.723,0],[1.725,0],[2.172,0],[2.212,0],[2.215,0],[2.224,0],[2.23,0],[2.248,0],[2.25,0],[2.252,0],[2.612,0],[2.613,0],[2.617,0],[2.631,0],[2.632,0]],
    Cma: [[1.664,2.74],[1.678,2.74],[1.686,2.68],[1.687,2.66],[1.689,2.71],[1.702,2.71],[1.723,2.74],[1.725,2.66],[2.172,2.49],[2.212,2.52],[2.215,2.51],[2.224,2.55],[2.23,2.55],[2.248,2.51],[2.25,2.53],[2.252,2.58],[2.612,2.41],[2.613,2.51],[2.617,2.4],[2.631,2.43],[2.632,2.46]],
    Cma2: [[1.664,-2.4],[1.678,-3.6],[1.686,-2.5],[1.687,-2.5],[1.689,0],[1.702,-2.9],[1.723,0],[1.725,-2.5],[2.172,-4],[2.212,-4],[2.215,-2.8],[2.224,-4],[2.23,-4],[2.248,-4],[2.25,-4],[2.252,-10],[2.612,-4],[2.613,-7.2],[2.617,-4],[2.631,-4],[2.632,-4]],
    CmqCma: [[1.664,-6.3],[1.678,-4.9],[1.686,-3.2],[1.687,-2.3],[1.689,-4.7],[1.702,-6],[1.723,-1],[1.725,-3.8],[2.172,-4],[2.212,-6.1],[2.215,-6.9],[2.224,-5.5],[2.23,-4.4],[2.248,-4.1],[2.25,-5.9],[2.252,-5.1],[2.612,-6.6],[2.613,-11.4],[2.617,-6.3],[2.631,-5.7],[2.632,-5.5]],
    CmqCma2: [[1.664,0],[1.678,0],[1.686,0],[1.687,0],[1.689,0],[1.702,0],[1.723,0],[1.725,0],[2.172,0],[2.212,0],[2.215,0],[2.224,0],[2.23,0],[2.248,0],[2.25,0],[2.252,0],[2.612,0],[2.613,0],[2.617,0],[2.631,0],[2.632,0]],
    Cnpa: [[1.664,0.07],[1.678,0.07],[1.686,-0.41],[1.687,-0.25],[1.689,0],[1.702,0.12],[1.723,-0.31],[1.725,-0.32],[2.172,-0.11],[2.212,0.09],[2.215,-0.07],[2.224,0.1],[2.23,0.03],[2.248,-0.15],[2.25,-0.02],[2.252,0.06],[2.612,0.09],[2.613,0.23],[2.617,0.08],[2.631,0.13],[2.632,0.1]],
    Cnpa2: [[1.664,2.6],[1.678,0],[1.686,54.5],[1.687,0],[1.689,0],[1.702,0],[1.723,0],[1.725,54.5],[2.172,0],[2.212,0],[2.215,23.5],[2.224,0],[2.23,0],[2.248,0],[2.25,0],[2.252,0],[2.612,0],[2.613,0],[2.617,0],[2.631,0],[2.632,0]],
    Clp: [[1.664,-0.0137],[1.678,-0.0143],[1.686,-0.016],[1.687,-0.0163],[1.689,-0.0151],[1.702,-0.0139],[1.723,-0.0235],[1.725,-0.0161],[2.172,-0.0124],[2.212,-0.0127],[2.215,-0.0121],[2.224,-0.0119],[2.23,-0.0134],[2.248,-0.0125],[2.25,-0.0176],[2.252,-0.0123],[2.612,-0.0148],[2.613,-0.0102],[2.617,-0.0148],[2.631,-0.0123],[2.632,-0.0129]],
    Cnq: [[0.00, 0.0]],
    Cnq2: [[0.00, 0.0]],
  },
  'Eley 22LR': {
    mass: '40',
    Ix: '.0909',
    Iy: '.291',
    diameter: '0.224',
    twistRate: '16',
    v0: '1000',
    Cd: [ [1.00, 0.435445], [0.95, 0.357320], [0.90, 0.303740], [0.85, 0.274705], [0.80, 0.270215], [0.75, 0.290270], [0.70, 0.334870] ],
    Cda2: [ [0.90, 2.653260], [0.85, 5.156966], [0.80, 6.664821], [0.75, 7.176827], [0.70, 6.692982] ],
    Cla: [ [0.773, 2.38], [0.800, 2.04], [0.828, 1.96], [0.861, 1.57], [0.885, 1.58], [0.923, 1.84] ],
    Cla2: [[0.00, 0.0]],
    Clp: [[0.00, -0.01]],
    Cma: [ [0.0, 1.07], [0.773, 1.07], [0.800, 1.07], [0.828, 1.11], [0.844, 1.08], [0.861, 1.10], [0.873, 1.15], [0.885, 1.24], [0.896, 1.26], [0.911, 1.33], [0.923, 1.48], [0.953, 1.83], [1.000, 1.72], ],
    Cma2: [[0.00, 0.0]],
    CmqCma: [ [0.0, -0.8], [0.773, -0.8], [0.8, -1.1], [0.828, 0], [0.873, 0.9], [0.896, 0.8], [0.923, -3.1], [1, -5] ],
    CmqCma2: [[0.00, 0.0]],
    Cnpa: [ [0, -0.1], [0.773, -0.1], [0.8, -0.1], [0.828, -0.3], [0.873, -0.4], [0.896, -0.5], [0.923, 0], [1, 0] ],
    Cnpa2: [[0.00, 0.0]],
    Cypa: [[0.00, 0.0]],
    Cypa2: [[0.00, 0.0]],
    Cnq: [[0.00, 0.0]],
    Cnq2: [[0.00, 0.0]],
  },
  '168 SMK': {
    mass: '168',
    Ix: '.7151',
    Iy: '5.197',
    diameter: '0.308',
    twistRate: '12',
    v0: '2600',
    Cd: [[0.00, 0.140], [0.80, 0.140], [0.85, 0.142], [0.90, 0.160], [0.95, 0.240], [1.00, 0.430], [1.05, 0.449], [1.10, 0.447], [1.20, 0.434], [1.40, 0.410], [1.60, 0.385], [1.80, 0.365], [2.00, 0.350], [2.20, 0.339], [2.50, 0.320]],
    Cda2: [[0.00, 2.9], [0.95, 2.9], [1.00, 3.0], [1.05, 3.1], [1.10, 3.6], [1.20, 6.5], [1.40, 7.6], [1.60, 7.3], [1.80, 6.8], [2.00, 6.1], [2.20, 5.4], [2.50, 4.4]],
    Cla: [[0.00, 1.75], [0.50, 1.63], [0.80, 1.45], [0.85, 1.40], [0.90, 1.35], [0.95, 1.30], [1.00, 1.35], [1.05, 1.55], [1.10, 1.70], [1.20, 1.90], [1.40, 2.15], [1.60, 2.32], [1.80, 2.45], [2.00, 2.58], [2.20, 2.68], [2.50, 2.85]],
    Cla2: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
    Clp: [[0.00, -0.0150], [0.50, -0.0125], [0.80, -0.0108], [0.85, -0.0107], [0.90, -0.0105], [0.95, -0.0103], [1.00, -0.0100], [1.05, -0.0099], [1.10, -0.0098], [1.20, -0.0095], [1.40, -0.0088], [1.60, -0.0083], [1.80, -0.0080], [2.00, -0.0075], [2.20, -0.0073], [2.50, -0.0068]],
    Cma: [[0.00, 3.05], [0.50, 3.26], [0.80, 3.38], [0.85, 3.40], [0.90, 3.43], [0.95, 3.45], [1.00, 3.24], [1.05, 3.17], [1.10, 3.15], [1.20, 3.12], [1.40, 3.06], [1.60, 2.98], [1.80, 2.88], [2.00, 2.79], [2.20, 2.69], [2.50, 2.56]],
    Cma2: [[0.00, -4.30], [0.95, -4.30], [1.00, -4.35], [1.05, -4.40], [2.50, -4.40]],
    CmqCma: [[0.00, 1.20], [1.05, 1.20], [1.10, 0.50], [1.20, -3.6], [1.40, -7.3], [1.60, -8.2], [2.50, -8.2]],
    CmqCma2: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
    Cnpa: [[0.00, -2.60], [0.90, -2.60], [1.10, -1.35], [1.40, -0.51], [1.70, -0.33], [2.5, -0.33]],
    Cnpa2: [[0.00, 50], [0.90, 50], [1.10, 50], [1.40, 50], [1.70, 50], [2.5, 50]],
    Cypa: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
    Cypa2: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
    Cnq: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
    Cnq2: [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]],
  },
  'M118': {
    mass: '175',
    Ix: '.716',
    Iy: '6.78',
    diameter: '0.308',
    twistRate: '12',
    v0: '2600',
    Cd: [ [ 0.798, 0.2425 ], [ 0.803, 0.1459 ], [ 1.086, 0.4968 ], [ 1.098, 0.4326 ], [ 1.395, 0.3907 ], [ 1.407, 0.4629 ], [ 1.801, 0.3436 ], [ 1.848, 0.3361 ], [ 2.181, 0.3219 ], [ 2.184, 0.3263 ], [ 2.191, 0.329 ] ],
    Cma: [ [ 0.798, 3.16 ], [ 0.803, 3.35 ], [ 1.086, 3.09 ], [ 1.098, 3.18 ], [ 1.395, 2.98 ], [ 1.407, 2.89 ], [ 1.801, 2.72 ], [ 1.848, 2.78 ], [ 2.181, 2.55 ], [ 2.184, 2.57 ], [ 2.191, 2.54 ] ],
    Cla: [ [ 0.798, 1.47 ], [ 0.803, 1.41 ], [ 1.086, 1.64 ], [ 1.098, 1.72 ], [ 1.395, 2.37 ], [ 1.407, 2.12 ], [ 2.181, 2.59 ], [ 2.184, 2.6 ], [ 2.191, 2.61 ] ],
    Cnpa: [ [ 0.798, -0.01 ], [ 0.803, -0.27 ], [ 1.086, 0.16 ], [ 1.098, -0.35 ], [ 1.395, -0.12 ], [ 1.407, 0.32 ], [ 1.801, 0.07 ], [ 1.848, -0.17 ], [ 2.181, 0.13 ], [ 2.184, 0.23 ], [ 2.191, 0.1 ] ],
    CmqCma: [ [ 0.798, -2.3 ], [ 0.803, 0.14 ], [ 1.086, -5.8 ], [ 1.098, 1.47 ], [ 1.395, -3.16 ], [ 1.407, -8.1 ], [ 1.801, -5.99 ], [ 1.848, -4.04 ], [ 2.181, -6.35 ], [ 2.184, -6.87 ], [ 2.191, -5.81 ] ],
    Cda2: [[0.5,3.168],[0.6,3.262],[0.7,3.448],[0.8,3.72],[0.9,4.066],[1,4.462],[1.1,4.87],[1.2,5.245],[1.3,5.549],[1.4,5.756],[1.5,5.864],[1.6,5.888],[1.7,5.854],[1.8,5.785],[1.9,5.705],[2,5.632],[2.1,5.576],[2.2,5.547],[2.3,5.541],[2.4,5.556],[2.5,5.588],[2.6,5.63],[2.7,5.674],[2.8,5.71],[2.9,5.731]],
    Cla2: [[0,0]],
    Clp: [[0,-0.01]],
    Cma2: [[0,0]],
    CmqCma2: [[0,0]],
    Cnpa2: [[0,0]],
    Cypa: [[0,0]],
    Cypa2: [[0,0]],
    Cnq: [[0,0]],
    Cnq2: [[0,0]],
  },
  'M33': {
    mass: '650',
    Ix: '7.85',
    Iy: '74.5',
    diameter: '0.510',
    twistRate: '14',
    v0: '2800',
    Cd: [ [ 0.551, 0.1324 ], [ 0.606, 0.123 ], [ 0.888, 0.1372 ], [ 0.918, 0.1505 ], [ 0.989, 0.3215 ], [ 1.003, 0.3461 ], [ 1.041, 0.3569 ], [ 1.158, 0.3757 ], [ 1.222, 0.3725 ], [ 1.475, 0.3637 ], [ 1.516, 0.3489 ], [ 1.953, 0.341 ], [ 1.972, 0.3171 ], [ 2.57, 0.2923 ], [ 2.589, 0.2971 ], [ 2.653, 0.2813 ] ],
    Cma: [ [ 0.551, 3.54 ], [ 0.606, 3.71 ], [ 0.888, 4.42 ], [ 0.918, 4.39 ], [ 0.989, 4.24 ], [ 1.003, 4.19 ], [ 1.041, 4.04 ], [ 1.158, 3.85 ], [ 1.222, 3.82 ], [ 1.475, 3.7 ], [ 1.516, 3.69 ], [ 1.953, 3.39 ], [ 1.972, 3.4 ],  [ 2.57, 3.07 ], [ 2.589, 3.01 ], [ 2.653, 3.01 ] ],
    Cla: [ [ 0.551, 1.67 ], [ 0.888, 1.44 ], [ 0.989, 1.45 ], [ 1.003, 1.65 ], [ 1.041, 1.46 ], [ 1.158, 1.79 ], [ 1.222, 1.89 ], [ 1.475, 2.06 ], [ 1.953, 2.43 ], [ 1.972, 2.29 ], [ 2.57, 2.2 ],   [ 2.589, 2.41 ], [ 2.653, 2.21 ] ],
    Cnpa: [ [ 0.918, 0.74 ], [ 1.475, 0.1 ], [ 1.953, 0.2 ], [ 1.972, 0.12 ], [ 2.57, 0.15 ], [ 2.589, 0.15 ], [ 2.653, 0.15 ] ],
    CmqCma: [ [ 0.606, 3.0 ], [ 0.888, 3.7 ],   [ 0.918, -12.6 ], [ 0.989, 1.0 ],   [ 1.003, -7.3 ], [ 1.041, 0.8 ],   [ 1.158, -4.6 ], [ 1.222, -3.9 ],   [ 1.475, -7.1 ], [ 1.516, -6.6 ],   [ 1.953, -8.4 ], [ 1.972, -8.8 ], [ 2.57, -6.2 ], [ 2.589, -6.4 ], [ 2.653, -7.4 ] ],
    Cda2: [[0,0]],
    Cla2: [[0,0]],
    Clp: [[0,-0.01]],
    Cma2: [[0,0]],
    CmqCma2: [[0,0]],
    Cnpa2: [[0,0]],
    Cypa: [[0,0]],
    Cypa2: [[0,0]],
    Cnq: [[0,0]],
    Cnq2: [[0,0]],
  },
  '140VLD': {
    Ix: '0.426',
    Iy: '5.667',
    mass: '140',
    diameter: '.264',
    twistRate: '7',
    v0: '2700',
    Cd: [ [ 0.5, 0.129 ], [ 0.6, 0.128 ], [ 0.7, 0.128 ], [ 0.8, 0.131 ], [ 0.9, 0.144 ], [ 1, 0.36 ], [ 1.1, 0.341 ], [ 1.2, 0.342 ], [ 1.3, 0.336 ], [ 1.4, 0.327 ], [ 1.5, 0.316 ], [ 1.6, 0.306 ], [ 1.7, 0.297 ], [ 1.8, 0.289 ], [ 1.9, 0.283 ], [ 2, 0.278 ], [ 2.1, 0.273 ], [ 2.2, 0.269 ], [ 2.3, 0.264 ], [ 2.4, 0.259 ], [ 2.5, 0.254 ], [ 2.6, 0.25 ], [ 2.7, 0.245 ], [ 2.8, 0.24 ], [ 2.9, 0.235 ] ],
    Cda2: [ [ 0.5, 3.345 ], [ 0.6, 3.444 ], [ 0.7, 3.639 ], [ 0.8, 3.925 ], [ 0.9, 4.282 ], [ 1, 4.685 ], [ 1.1, 5.096 ], [ 1.2, 5.472 ], [ 1.3, 5.78 ],  [ 1.4, 6.001 ], [ 1.5, 6.136 ], [ 1.6, 6.202 ], [ 1.7, 6.224 ], [ 1.8, 6.221 ], [ 1.9, 6.218 ], [ 2, 6.23 ], [ 2.1, 6.263 ], [ 2.2, 6.325 ], [ 2.3, 6.413 ], [ 2.4, 6.521 ], [ 2.5, 6.642 ], [ 2.6, 6.766 ], [ 2.7, 6.879 ], [ 2.8, 6.966 ], [ 2.9, 7.013 ] ],
    Cla: [ [ 0.5, 1.504 ], [ 0.6, 1.536 ], [ 0.7, 1.595 ], [ 0.8, 1.675 ], [ 0.9, 1.765 ], [ 1, 1.857 ], [ 1.1, 1.944 ], [ 1.2, 2.021 ], [ 1.3, 2.087 ], [ 1.4, 2.144 ], [ 1.5, 2.192 ], [ 1.6, 2.235 ], [ 1.7, 2.271 ], [ 1.8, 2.304 ], [ 1.9, 2.332 ], [ 2, 2.358 ], [ 2.1, 2.38 ],  [ 2.2, 2.399 ], [ 2.3, 2.416 ], [ 2.4, 2.431 ], [ 2.5, 2.444 ], [ 2.6, 2.455 ], [ 2.7, 2.464 ], [ 2.8, 2.471 ], [ 2.9, 2.474 ] ],
    Cma: [ [ 0.5, 4.228 ], [ 0.6, 4.232 ], [ 0.7, 4.239 ], [ 0.8, 4.243 ], [ 0.9, 4.238 ], [ 1, 4.222 ], [ 1.1, 4.194 ], [ 1.2, 4.158 ], [ 1.3, 4.118 ], [ 1.4, 4.076 ], [ 1.5, 4.034 ], [ 1.6, 3.992 ], [ 1.7, 3.95 ],  [ 1.8, 3.91 ], [ 1.9, 3.87 ],  [ 2, 3.834 ], [ 2.1, 3.799 ], [ 2.2, 3.766 ], [ 2.3, 3.736 ], [ 2.4, 3.709 ], [ 2.5, 3.685 ], [ 2.6, 3.664 ], [ 2.7, 3.647 ], [ 2.8, 3.635 ], [ 2.9, 3.629 ] ],
    Clp: [ [ 0.5, -0.03 ],  [ 0.6, -0.029 ], [ 0.7, -0.028 ], [ 0.8, -0.028 ], [ 0.9, -0.027 ], [ 1, -0.026 ], [ 1.1, -0.026 ], [ 1.2, -0.025 ], [ 1.3, -0.024 ], [ 1.4, -0.024 ], [ 1.5, -0.023 ], [ 1.6, -0.023 ], [ 1.7, -0.022 ], [ 1.8, -0.021 ], [ 1.9, -0.021 ], [ 2, -0.02 ], [ 2.1, -0.02 ],  [ 2.2, -0.019 ], [ 2.3, -0.019 ], [ 2.4, -0.018 ], [ 2.5, -0.018 ], [ 2.6, -0.018 ], [ 2.7, -0.017 ], [ 2.8, -0.017 ], [ 2.9, -0.016 ] ],
    CmqCma: [ [ 0.5, -7.728 ],  [ 0.6, -8.01 ], [ 0.7, -8.617 ],  [ 0.8, -9.49 ], [ 0.9, -10.603 ], [ 1, -11.88 ], [ 1.1, -13.201 ], [ 1.2, -14.439 ], [ 1.3, -15.492 ], [ 1.4, -16.304 ], [ 1.5, -16.877 ], [ 1.6, -17.249 ], [ 1.7, -17.474 ], [ 1.8, -17.606 ], [ 1.9, -17.648 ], [ 2, -17.682 ], [ 2.1, -17.705 ], [ 2.2, -17.721 ], [ 2.3, -17.733 ], [ 2.4, -17.742 ], [ 2.5, -17.749 ], [ 2.6, -17.754 ], [ 2.7, -17.758 ], [ 2.8, -17.76 ], [ 2.9, -17.762 ] ],
    Cnpa: [ [ 0.5, 0.11 ],  [ 0.6, 0.111 ], [ 0.7, 0.11 ],  [ 0.8, 0.108 ], [ 0.9, 0.107 ], [ 1, 0.08 ], [ 1.1, 0.401 ], [ 1.2, 0.379 ], [ 1.3, 0.389 ], [ 1.4, 0.397 ], [ 1.5, 0.397 ], [ 1.6, 0.396 ], [ 1.7, 0.394 ], [ 1.8, 0.393 ], [ 1.9, 0.392 ], [ 2, 0.391 ], [ 2.1, 0.39 ],  [ 2.2, 0.389 ], [ 2.3, 0.388 ], [ 2.4, 0.388 ], [ 2.5, 0.387 ], [ 2.6, 0.386 ], [ 2.7, 0.386 ], [ 2.8, 0.385 ], [ 2.9, 0.385 ] ],
    Cla2: [[0,0]],
    Cnpa2: [[0,0]],
    Cma2: [[0,0]],
    CmqCma2: [[0,0]],
    Cypa: [[0,0]],
    Cypa2: [[0,0]],
    Cnq: [[0,0]],
    Cnq2: [[0,0]],
  },
  '300 sierra': {
    Ix: 1.535,
    Iy: 19.156,
    mass: 299.9,
    diameter: 0.338,
    twistRate: 11.9,
    v0: 2800,
    Cd: [[0.5,0.116],[0.6,0.114],[0.7,0.114],[0.8,0.117],[0.9,0.132],[1,0.409],[1.1,0.376],[1.2,0.362],[1.3,0.36],[1.4,0.352],[1.5,0.34],[1.6,0.328],[1.7,0.318],[1.8,0.31],[1.9,0.303],[2,0.297],[2.1,0.292],[2.2,0.286],[2.3,0.281],[2.4,0.276],[2.5,0.271],[2.6,0.266],[2.7,0.261],[2.8,0.256],[2.9,0.251]],
    Cda2: [[0.5,3.221],[0.6,3.321],[0.7,3.517],[0.8,3.805],[0.9,4.167],[1,4.579],[1.1,5.003],[1.2,5.395],[1.3,5.72],[1.4,5.957],[1.5,6.107],[1.6,6.187],[1.7,6.22],[1.8,6.231],[1.9,6.241],[2,6.267],[2.1,6.318],[2.2,6.4],[2.3,6.51],[2.4,6.642],[2.5,6.788],[2.6,6.936],[2.7,7.07],[2.8,7.172],[2.9,7.228]],
    Cla: [[0.5,1.395],[0.6,1.427],[0.7,1.487],[0.8,1.569],[0.9,1.664],[1,1.763],[1.1,1.86],[1.2,1.95],[1.3,2.031],[1.4,2.102],[1.5,2.163],[1.6,2.217],[1.7,2.264],[1.8,2.306],[1.9,2.342],[2,2.375],[2.1,2.403],[2.2,2.428],[2.3,2.451],[2.4,2.471],[2.5,2.488],[2.6,2.503],[2.7,2.515],[2.8,2.524],[2.9,2.528]],
    Cma: [[0.5,4.147],[0.6,4.156],[0.7,4.168],[0.8,4.177],[0.9,4.175],[1,4.155],[1.1,4.117],[1.2,4.063],[1.3,4],[1.4,3.933],[1.5,3.866],[1.6,3.803],[1.7,3.744],[1.8,3.69],[1.9,3.642],[2,3.598],[2.1,3.558],[2.2,3.522],[2.3,3.489],[2.4,3.459],[2.5,3.433],[2.6,3.41],[2.7,3.392],[2.8,3.379],[2.9,3.372]],
    Clp: [[0.5,-0.032],[0.6,-0.031],[0.7,-0.031],[0.8,-0.03],[0.9,-0.029],[1,-0.028],[1.1,-0.028],[1.2,-0.027],[1.3,-0.026],[1.4,-0.026],[1.5,-0.025],[1.6,-0.024],[1.7,-0.024],[1.8,-0.023],[1.9,-0.023],[2,-0.022],[2.1,-0.021],[2.2,-0.021],[2.3,-0.02],[2.4,-0.02],[2.5,-0.019],[2.6,-0.019],[2.7,-0.019],[2.8,-0.018],[2.9,-0.018]],
    CmqCma: [[0.5,-7.614],[0.6,-7.908],[0.7,-8.533],[0.8,-9.432],[0.9,-10.579],[1,-11.892],[1.1,-13.251],[1.2,-14.525],[1.3,-15.607],[1.4,-16.445],[1.5,-17.041],[1.6,-17.435],[1.7,-17.682],[1.8,-17.837],[1.9,-17.906],[2,-17.964],[2.1,-18.013],[2.2,-18.054],[2.3,-18.089],[2.4,-18.12],[2.5,-18.146],[2.6,-18.168],[2.7,-18.185],[2.8,-18.197],[2.9,-18.203]],
    Cnpa: [[0.5,-0.087],[0.6,-0.077],[0.7,-0.066],[0.8,-0.055],[0.9,-0.044],[1,-0.033],[1.1,-0.022],[1.2,-0.01],[1.3,0.002],[1.4,0.013],[1.5,0.025],[1.6,0.036],[1.7,0.047],[1.8,0.058],[1.9,0.069],[2,0.08],[2.1,0.09],[2.2,0.1],[2.3,0.109],[2.4,0.118],[2.5,0.127],[2.6,0.136],[2.7,0.144],[2.8,0.152],[2.9,0.159]],
    Cla2: [[0,0]],
    Cnpa2: [[0,0]],
    Cma2: [[0,0]],
    CmqCma2: [[0,0]],
    Cypa: [[0,0]],
    Cypa2: [[0,0]],
    Cnq: [[0,0]],
    Cnq2: [[0,0]]
  },
  '300 hybrid': {
    Ix: 1.530,
    Iy: 19.796,
    mass: 300.2,
    diameter: 0.338,
    twistRate: 11.0,
    v0: 2800,
    Cd: [[0.5,0.12],[0.6,0.118],[0.7,0.118],[0.8,0.121],[0.9,0.134],[1,0.346],[1.1,0.331],[1.2,0.331],[1.3,0.323],[1.4,0.313],[1.5,0.302],[1.6,0.291],[1.7,0.283],[1.8,0.275],[1.9,0.269],[2,0.264],[2.1,0.259],[2.2,0.254],[2.3,0.249],[2.4,0.244],[2.5,0.24],[2.6,0.235],[2.7,0.23],[2.8,0.226],[2.9,0.221]],
    Cda2: [[0.5,3.274],[0.6,3.372],[0.7,3.565],[0.8,3.848],[0.9,4.202],[1,4.603],[1.1,5.013],[1.2,5.392],[1.3,5.703],[1.4,5.927],[1.5,6.066],[1.6,6.136],[1.7,6.16],[1.8,6.162],[1.9,6.166],[2,6.184],[2.1,6.227],[2.2,6.302],[2.3,6.404],[2.4,6.528],[2.5,6.666],[2.6,6.807],[2.7,6.935],[2.8,7.033],[2.9,7.086]],
    Cla: [[0.5,1.498],[0.6,1.535],[0.7,1.602],[0.8,1.692],[0.9,1.791],[1,1.89],[1.1,1.979],[1.2,2.057],[1.3,2.122],[1.4,2.178],[1.5,2.226],[1.6,2.268],[1.7,2.305],[1.8,2.338],[1.9,2.368],[2,2.394],[2.1,2.417],[2.2,2.436],[2.3,2.453],[2.4,2.467],[2.5,2.48],[2.6,2.49],[2.7,2.499],[2.8,2.504],[2.9,2.507]],
    Cma: [[0.5,4.383],[0.6,4.392],[0.7,4.407],[0.8,4.422],[0.9,4.429],[1,4.425],[1.1,4.407],[1.2,4.378],[1.3,4.341],[1.4,4.3],[1.5,4.257],[1.6,4.212],[1.7,4.167],[1.8,4.122],[1.9,4.08],[2,4.04],[2.1,4.003],[2.2,3.969],[2.3,3.938],[2.4,3.91],[2.5,3.885],[2.6,3.864],[2.7,3.848],[2.8,3.836],[2.9,3.83]],
    Clp: [[0.5,-0.033],[0.6,-0.032],[0.7,-0.031],[0.8,-0.031],[0.9,-0.03],[1,-0.029],[1.1,-0.028],[1.2,-0.028],[1.3,-0.027],[1.4,-0.026],[1.5,-0.026],[1.6,-0.025],[1.7,-0.024],[1.8,-0.024],[1.9,-0.023],[2,-0.022],[2.1,-0.022],[2.2,-0.021],[2.3,-0.021],[2.4,-0.02],[2.5,-0.02],[2.6,-0.019],[2.7,-0.019],[2.8,-0.019],[2.9,-0.018]],
    CmqCma: [[0.5,-8.546],[0.6,-8.889],[0.7,-9.615],[0.8,-10.653],[0.9,-11.969],[1,-13.466],[1.1,-15.006],[1.2,-16.445],[1.3,-17.665],[1.4,-18.609],[1.5,-19.279],[1.6,-19.721],[1.7,-19.994],[1.8,-20.161],[1.9,-20.227],[2,-20.28],[2.1,-20.319],[2.2,-20.349],[2.3,-20.371],[2.4,-20.389],[2.5,-20.403],[2.6,-20.413],[2.7,-20.421],[2.8,-20.427],[2.9,-20.43]],
    Cnpa: [[0.5,-0.07],[0.6,-0.053],[0.7,-0.036],[0.8,-0.02],[0.9,-0.003],[1,0.014],[1.1,0.031],[1.2,0.048],[1.3,0.065],[1.4,0.081],[1.5,0.097],[1.6,0.112],[1.7,0.127],[1.8,0.142],[1.9,0.156],[2,0.17],[2.1,0.183],[2.2,0.196],[2.3,0.208],[2.4,0.22],[2.5,0.231],[2.6,0.241],[2.7,0.251],[2.8,0.261],[2.9,0.27]],
    Cla2: [[0,0]],
    Cnpa2: [[0,0]],
    Cma2: [[0,0]],
    CmqCma2: [[0,0]],
    Cypa: [[0,0]],
    Cypa2: [[0,0]],
    Cnq: [[0,0]],
    Cnq2: [[0,0]]
  }
}

const CoefficientTable = ({ name, data, onChange }) => {
  const handleAddRow = () => {
    onChange([...data, [0, 0]])
  }

  const handleDeleteRow = (index) => {
    onChange(data.filter((_, i) => i !== index))
  }

  const handleChange = (index, field, value) => {
    const newData = [...data]
    newData[index][field] = parseFloat(value) || 0
    onChange(newData)
  }

  const handleBlur = () => {
    const newData = [...data]
    newData.sort((a, b) => a[0] - b[0])
    onChange(newData)
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ p: 2 }}>{name}</Typography>
      <Box sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.map(([mach, value]) => ({ mach, value }))}>
            <XAxis dataKey="mach" label={{ value: 'Mach', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: 'Value', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#8884d8" dot={true} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mach</TableCell>
            <TableCell>Value</TableCell>
            <TableCell align="right">
              <IconButton size="small" onClick={handleAddRow}>
                <AddIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row[0]}
                  onChange={e => handleChange(index, 0, e.target.value)}
                  onBlur={handleBlur}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row[1]}
                  onChange={e => handleChange(index, 1, e.target.value)}
                  onBlur={handleBlur}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleDeleteRow(index)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const SixDof = () => {
  const defaults = examples['168 SMK'];
  // 168 SMK
  const [mass, setMass] = useAutoSaveState('mass', defaults.mass) // grains
  const [Ix, setIx] = useAutoSaveState('Ix', defaults.Ix) // g.cm^2
  const [Iy, setIy] = useAutoSaveState('Iy', defaults.Iy) // g.cm^2
  const [diameter, setDiameter] = useAutoSaveState('diameter', defaults.diameter) // inches
  const [twistRate, setTwistRate] = useAutoSaveState('twistRate', defaults.twistRate) // inches per turn
  const [v0, setV0] = useAutoSaveState('v0', defaults.v0) // fps

  // Initial Conditions
  const [phi0, setPhi0] = useAutoSaveState('phi0', '0.0006')
  const [theta0, setTheta0] = useAutoSaveState('theta0', '0')
  const [pitchRate0, setPitchRate0] = useAutoSaveState('pitchRate0', '25')
  const [yawRate0, setYawRate0] = useAutoSaveState('yawRate0', '0')
  const [alpha0, setAlpha0] = useAutoSaveState('alpha0', '0')
  const [beta0, setBeta0] = useAutoSaveState('beta0', '0')

  // Environment
  const [pressure, setPressure] = useAutoSaveState('pressure', '29.92') // inHg
  const [temperature, setTemperature] = useAutoSaveState('temperature', '59') // F
  const [windSpeed, setWindSpeed] = useAutoSaveState('windSpeed', '10') // mph
  const [windDirection, setWindDirection] = useAutoSaveState('windDirection', '90')
  const [azimuth, setAzimuth] = useAutoSaveState('azimuth', '0') // deg
  const [latitude, setLatitude] = useAutoSaveState('latitude', '0') // deg

  // Control Parameters
  const [maxTime, setMaxTime] = useAutoSaveState('maxTime', '0.12')

  const [result, setResult] = useState('')
  const canvasRef = useRef(null)
  const canvasRef2 = useRef(null)
  const [formTab, setFormTab] = useState(0);
  const [coeffTab, setCoeffTab] = useState(0);
  const [resultsTab, setResultsTab] = useState(0);

  // 168 SMK
  const [Cd, setCd] = useAutoSaveState('Cd', defaults.Cd)
  const [Cda2, setCda2] = useAutoSaveState('Cda2', defaults.Cda2)
  const [Cla, setCla] = useAutoSaveState('Cla', defaults.Cla)
  const [Cla2, setCla2] = useAutoSaveState('Cla2', defaults.Cla2)
  const [Clp, setClp] = useAutoSaveState('Clp', defaults.Clp)
  const [Cma, setCma] = useAutoSaveState('Cma', defaults.Cma)
  const [Cma2, setCma2] = useAutoSaveState('Cma2', defaults.Cma2)
  const [CmqCma, setCmqCma] = useAutoSaveState('CmqCma', defaults.CmqCma)
  const [CmqCma2, setCmqCma2] = useAutoSaveState('CmqCma2', defaults.CmqCma2)
  const [Cnpa, setCnpa] = useAutoSaveState('Cnpa', defaults.Cnpa)
  const [Cnpa2, setCnpa2] = useAutoSaveState('Cnpa2', defaults.Cnpa2)
  const [Cypa, setCypa] = useAutoSaveState('Cypa', defaults.Cypa)
  const [Cypa2, setCypa2] = useAutoSaveState('Cypa2', defaults.Cypa2)
  const [Cnq, setCnq] = useAutoSaveState('Cnq', defaults.Cnq)
  const [Cnq2, setCnq2] = useAutoSaveState('Cnq2', defaults.Cnq2)

  useEffect(() => {
    if (result) {
      drawAlphaBeta(result)
      drawPos(result)
      //console.log(result.filter((v, i) => i % 50 === 0).map(v => `(${v.time.toFixed(5)}, ${v.mach.toFixed(3)})`).join(' '))
      //console.log(result.filter((v, i) => i % 50 === 0).map(v => `(${v.time.toFixed(5)}, ${v.Sg.toFixed(3)})`).join(' '))
      //console.log(result.filter((v, i) => i % 50 === 0).map(v => `(${v.time.toFixed(5)}, ${Math.min(Math.max(v.Sd, -7), 7).toFixed(3)})`).join(' '))
      //console.log(result.filter((v, i) => i % 50 === 0).map(v => `(${v.time.toFixed(5)}, ${v.alphaTDeg.toFixed(3)})`).join(' '))
      //console.log('cd',   result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.cd.toFixed(3)})`).join(' '))
      //console.log('cla',  result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.clα.toFixed(3)})`).join(' '))
      //console.log('cma',  result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.cmα.toFixed(3)})`).join(' '))
      //console.log('cmq',  result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.cmq.toFixed(3)})`).join(' '))
      //console.log('clp',  result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.clp.toFixed(3)})`).join(' '))
      //console.log('Cnpa', result.filter((v, i) => i % 100 === 0).map(v => `(${v.time.toFixed(5)}, ${v.Cnpα.toFixed(3)})`).join(' '))
    }
  }, [result])

  // Redraw canvases when switching to Angles tab
  useEffect(() => {
    if (result && resultsTab === 0) {
      // Small delay to ensure canvas elements are rendered
      setTimeout(() => {
        drawAlphaBeta(result)
        drawPos(result)
      }, 100)
    }
  }, [result, resultsTab])

  const loadExample = name => {
    const example = examples[name]
    if (!example) return
    setMass(example.mass)
    setIx(example.Ix)
    setIy(example.Iy)
    setDiameter(example.diameter)
    setTwistRate(example.twistRate)
    setV0(example.v0)
    setCd(example.Cd)
    setCda2(example.Cda2)
    setCla(example.Cla)
    setCla2(example.Cla2)
    setClp(example.Clp)
    setCma(example.Cma)
    setCma2(example.Cma2)
    setCmqCma(example.CmqCma)
    setCmqCma2(example.CmqCma2)
    setCnpa(example.Cnpa)
    setCnpa2(example.Cnpa2)
    setCypa(example.Cypa)
    setCypa2(example.Cypa2)
    setCnq(example.Cnq)
    setCnq2(example.Cnq2)
  }

  const calculate = () => {
    const winds = [
      {
        x: mphToMps(parseFloat(windSpeed)) * Math.cos(degToRad(windDirection) + Math.PI),
        y: 0,
        z: mphToMps(parseFloat(windSpeed)) * Math.sin(degToRad(windDirection) + Math.PI)
      }
    ]
    let simulation = new SixDofSim({
      mass: grainsToKg(parseFloat(mass)),
      Ix: gcm2ToKgm2(parseFloat(Ix)),
      Iy: gcm2ToKgm2(parseFloat(Iy)),
      pressure: inhgToPa(parseFloat(pressure)),
      diameter: inchesToMeters(parseFloat(diameter)),
      temperature: fToK(parseFloat(temperature)),
      winds,
      azimuth: degToRad(parseFloat(azimuth)),
      latitude: degToRad(parseFloat(latitude)),
      incline: 0,
      altitude: 0,
      humidity: 0,
      x0: 0,
      y0: 0,
      z0: 0,
      v0: fpsToMps(parseFloat(v0)),
      calibersPerTurn: parseFloat(twistRate) / parseFloat(diameter),
      phi0: parseFloat(phi0),
      theta0: parseFloat(theta0),
      pitchRate0: parseFloat(pitchRate0),
      yawRate0: parseFloat(yawRate0),
      alpha0: parseFloat(alpha0),
      beta0: parseFloat(beta0),
      Cd,
      Cda2,
      Clp,
      Cla,
      Cla2,
      Cma,
      Cma2,
      CmqCma,
      CmqCma2,
      Cnpa,
      Cnpa2,
      Cypa,
      Cypa2,
      Cnq,
      Cnq2,
    })
    const start = Date.now()
    const newResult = simulation.integrate({
      maxTime: parseFloat(maxTime),
      resultInterval: parseFloat(maxTime) / 1000
    })
    const end = Date.now()
    console.log('Calculation time:', (end - start) / 1000, 's')
    setResult(newResult)
    window.gtag('event', 'calculate_6dof')
  }

  const drawAlphaBeta = result => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // scale to fit canvas
    // find scale to fit the canvas
    const maxAlpha = Math.max(...result.map(({ alpha }) => Math.abs(alpha)))
    const maxBeta = Math.max(...result.map(({ beta }) => Math.abs(beta)))
    const scale = Math.min(canvas.width / maxAlpha, canvas.height / maxBeta) * 0.5
    // draw the center
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 5, 0, Math.PI * 2)
    ctx.fill()
    // draw alpha vs beta
    //const points = result.filter(v => v.posX < 15)
    //const points = result.filter(v => v.posX > 100 && v.posX < 200)
    //const points = result.filter(v => v.posX > 850 && v.posX < 875)
    //const points = result.filter(v => v.posX > 875 && v.posX < 900)
    const points = result
    //const points = result.filter(v => v.posX > 15 && v.posX < 25.5)
    //const points = result.filter(v => v.posX > 105 && v.posX < 171)
    points.forEach(({ alpha, beta, posX }, i) => {
      //if (i === 0) return // skip first point
      // make darker for each step
      const prev = points[i - 1] || { alpha: 0, beta: 0 }
      ctx.strokeStyle = `rgba(${255 * (i / points.length)}, 0, ${255 * (1 - i / points.length)}, 1)`
      ctx.beginPath()
      ctx.moveTo(canvas.width / 2 + prev.beta * scale, canvas.height / 2 - prev.alpha * scale)
      ctx.lineTo(canvas.width / 2 + beta * scale, canvas.height / 2 - alpha * scale)
      ctx.stroke()
    })
  }

  const drawPos = result => {
    const canvas = canvasRef2.current
    if (!canvas) return
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const maxY = Math.max(...result.map(({ posY }) => Math.abs(posY)))
    const maxZ = Math.max(...result.map(({ posZ }) => Math.abs(posZ)))
    const scale = Math.min(canvas.width / maxY, canvas.height / maxZ) * 0.5
    // draw the center
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 5, 0, Math.PI * 2)
    ctx.fill()
    // draw alpha vs beta
    result.forEach(({ posY, posZ }, i) => {
      if (i === 0) return // skip first point
      // make darker for each step
      const prev = result[i - 1] || { posY: 0, posZ: 0 }
      ctx.strokeStyle = `rgba(${255 * (i / result.length)}, 0, ${255 * (1 - i / result.length)}, 1)`
      ctx.beginPath()
      ctx.moveTo(canvas.width / 2 + prev.posZ * scale, canvas.height / 2 - prev.posY * scale)
      ctx.lineTo(canvas.width / 2 + posZ * scale, canvas.height / 2 - posY * scale)
      ctx.stroke()
    })
  }

  const formatTooltip = (value) => {
    return parseFloat(value).toFixed(3);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            6 Degrees of Freedom Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />

        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={formTab} onChange={(e, v) => setFormTab(v)} variant="scrollable" scrollButtons="auto">
                <Tab label="Initial Conditions" />
                <Tab label="Bullet Properties" />
                <Tab label="Environment" />
                <Tab label="Coefficients" />
                <Tab label="Examples" />
              </Tabs>
            </Box>

            {formTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Max Time" value={maxTime} onChange={e => setMaxTime(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Initial Velocity" value={v0} onChange={e => setV0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Vertical Angle" value={phi0} onChange={e => setPhi0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Horizontal Angle" value={theta0} onChange={e => setTheta0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Pitch Rate" value={pitchRate0} onChange={e => setPitchRate0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad/s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Yaw Rate" value={yawRate0} onChange={e => setYawRate0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad/s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Alpha" value={alpha0} onChange={e => setAlpha0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Beta" value={beta0} onChange={e => setBeta0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Mass" value={mass} onChange={e => setMass(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Diameter" value={diameter} onChange={e => setDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Ix" value={Ix} onChange={e => setIx(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm²</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Iy" value={Iy} onChange={e => setIy(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm²</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Twist Rate" value={twistRate} onChange={e => setTwistRate(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Air Pressure" value={pressure} onChange={e => setPressure(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inHg</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Temperature" value={temperature} onChange={e => setTemperature(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Wind Speed" value={windSpeed} onChange={e => setWindSpeed(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">mph</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Wind Direction" value={windDirection} onChange={e => setWindDirection(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Latitude" value={latitude} onChange={e => setLatitude(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Azimuth" value={azimuth} onChange={e => setAzimuth(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 3 && (
              <Grid container spacing={2}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: '100%' }}>
                  <Tabs value={coeffTab} onChange={(e, v) => setCoeffTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Drag" />
                    <Tab label="Lift" />
                    <Tab label="Pitch" />
                    <Tab label="Roll" />
                    <Tab label="Magnus" />
                    <Tab label="Pitch Damping" />
                    <Tab label="Magnus Force" />
                    <Tab label="Pitch Damping Force" />
                  </Tabs>
                </Box>
                {coeffTab === 0 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>D<sub>0</sub></sub></span>} data={Cd} onChange={setCd} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>D<sub>α</sub>2</sub></span>} data={Cda2} onChange={setCda2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 1 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>L<sub>α</sub></sub></span>} data={Cla} onChange={setCla} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>L<sub>α</sub>2</sub></span>} data={Cla2} onChange={setCla2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 2 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>m<sub>α</sub></sub></span>} data={Cma} onChange={setCma} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>m<sub>α</sub>2</sub></span>} data={Cma2} onChange={setCma2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 3 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>lp</sub></span>} data={Clp} onChange={setClp} />
                    </Grid>
                  </>
                )}
                {coeffTab === 4 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>np<sub>α</sub></sub></span>} data={Cnpa} onChange={setCnpa} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>np<sub>α</sub>2</sub></span>} data={Cnpa2} onChange={setCnpa2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 5 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>(C<sub>mq</sub> + C<sub>mά</sub>)</span>} data={CmqCma} onChange={setCmqCma} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>(C<sub>mq</sub> + C<sub>mά</sub>)<sub>2</sub></span>} data={CmqCma2} onChange={setCmqCma2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 6 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>yp<sub>α</sub></sub></span>} data={Cypa} onChange={setCypa} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>C<sub>yp<sub>α</sub>2</sub></span>} data={Cypa2} onChange={setCypa2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 7 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>(C<sub>Nq</sub> + C<sub>Nά</sub>)</span>} data={Cnq} onChange={setCnq} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name={<span>(C<sub>Nq</sub> + C<sub>Nά</sub>)<sub>2</sub></span>} data={Cnq2} onChange={setCnq2} />
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {formTab === 4 && (
              <Grid container spacing={2}>
                {Object.keys(examples).map(name => (
                  <Grid item xs={4} sm={2} key={name}>
                    <Button variant="outlined" onClick={() => loadExample(name)}>
                      {name}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Calculate Button */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>

        {/* Results Section */}
        {result && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Grid container xs={12} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  <EqualizerIcon /> Results
                </Typography>
              </Grid>
              <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={resultsTab} onChange={(e, v) => setResultsTab(v)} variant="scrollable" scrollButtons="auto">
                  <Tab label="Plots" />
                  <Tab label="Angles" />
                  <Tab label="Velocity" />
                  <Tab label="Position" />
                  <Tab label="Motion" />
                  <Tab label="Stability" />
                  <Tab label="Coefficients" />
                </Tabs>
              </Box>

              {resultsTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={10}>
                    <Box display="flex" justifyContent="center" mb={1}>
                      Pitch vs Yaw
                    </Box>
                    <Box display="flex" justifyContent="space-around" mb={2}>
                      <canvas ref={canvasRef} style={{ border: '1px solid #aaa', width: '100%', aspectRatio: '1 / 1' }}/>
                    </Box>
                  </Grid>
                  <Grid item xs={10}>
                    <Box display="flex" justifyContent="center" mb={1}>
                      Vertical vs Horizontal Position
                    </Box>
                    <Box display="flex" justifyContent="space-around" mb={2}>
                      <canvas ref={canvasRef2} style={{ border: '1px solid #aaa', width: '100%', aspectRatio: '1 / 1' }}/>
                    </Box>
                  </Grid>
                </Grid>
              )}
              {resultsTab === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Alpha and Beta Angles</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          alphaDeg: radToDeg(point.alpha),
                          betaDeg: radToDeg(point.beta),
                          alphaTDeg: radToDeg(point.alphaT)
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="alphaDeg" stroke="#8884d8" name="Alpha (deg)" dot={false} />
                          <Line type="monotone" dataKey="betaDeg" stroke="#82ca9d" name="Beta (deg)" dot={false} />
                          <Line type="monotone" dataKey="alphaTDeg" stroke="#f2aa7d" name="AoA (deg)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {resultsTab === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 1000, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Velocity Components</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          v: mpsToFps(point.v),
                          vx: mpsToFps(point.vx),
                          vy: mpsToFps(point.vy),
                          vz: mpsToFps(point.vz)
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="v" stroke="#8884d8" name="Total Velocity (fps)" dot={false} />
                          <Line type="monotone" dataKey="vx" stroke="#82ca9d" name="Vx (fps)" dot={false} />
                          <Line type="monotone" dataKey="vy" stroke="#ffc658" name="Vy (fps)" dot={false} />
                          <Line type="monotone" dataKey="vz" stroke="#ff7300" name="Vz (fps)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Mach</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="mach" stroke="#c2418a" name="Mach" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {resultsTab === 3 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Range</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          posX: metersToYards(point.posX),
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="posX" stroke="#8884d8" name="X Position (yd)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Drop</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          posY: metersToInches(point.posY),
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="posY" stroke="#82ca9d" name="Y Position (in)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Wind Drift</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          posZ: metersToInches(point.posZ)
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="posZ" stroke="#ffc658" name="Z Position (in)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {resultsTab === 4 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Angular Rates</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="rollRate" stroke="#42caad" name="Roll Rate (rad/s)" dot={false} />
                          <Line type="monotone" dataKey="pitchRate" stroke="#82ca9d" name="Pitch Rate (rad/s)" dot={false} />
                          <Line type="monotone" dataKey="yawRate" stroke="#ffc658" name="Yaw Rate (rad/s)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Spin Rate</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="spinRate" stroke="#82ca9d" name="Spin Rate (rad/s)" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {resultsTab === 5 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Stability</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result.map(point => ({
                          ...point,
                          Sd: Math.min(Math.max(point.Sd, -10), 10),
                          Sg: Math.min(Math.max(point.Sg, -10), 10),
                        }))}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="Sg" stroke="#82ca9d" name="Sg" dot={false} />
                          <Line type="monotone" dataKey="Sd" stroke="#f42a32" name="Sd" dot={false} />
                          <Line type="monotone" dataKey="stable" stroke="#c2418a" name="stable" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {resultsTab === 6 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, mb: 4 }}>
                      <Typography variant="h6" gutterBottom>Coefficients</Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={result}>
                          <XAxis dataKey="time" />
                          <YAxis />
                          <Tooltip formatter={formatTooltip} />
                          <Legend />
                          <Line type="monotone" dataKey="cd" stroke="#8884d8" name="Cd" dot={false} />
                          <Line type="monotone" dataKey="cla" stroke="#82ca9d" name="Clα" dot={false} />
                          <Line type="monotone" dataKey="cma" stroke="#ffc658" name="Cmα" dot={false} />
                          <Line type="monotone" dataKey="cmq" stroke="#177340" name="Cmq" dot={false} />
                          <Line type="monotone" dataKey="clp" stroke="#2f73f0" name="Clp" dot={false} />
                          <Line type="monotone" dataKey="cnpa" stroke="#ff7300" name="Cnpα" dot={false} />
                          <Line type="monotone" dataKey="cypa" stroke="#f42a32" name="Cypα" dot={false} />
                          <Line type="monotone" dataKey="cnq" stroke="#c2418a" name="Cnq" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

export default function WrappedTemplate () {
  return <AutoSaveProvider>
    <SixDof />
  </AutoSaveProvider>
}
